import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SendOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Collapse,
  Empty,
  Input,
  Popconfirm,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
} from "antd";
import { debounce } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { PriorityMark, ResearchPurchaseRequestStatus } from "../../enum/common";
import { DepartmentCode } from "../../enum/roles";
import { IResearchPurchaseRequest, MaterialItem } from "../../models/Research";
import {
  useApprovePurchaseRequestByDirector,
  useCreatePurchaseRequest,
  useGetResearchProject,
  useGetResearchProjectPurchaseRequest,
  useRequestPurchaseRequestForDirector,
  useUpdateResearchProjectPurchaseRequest,
} from "../../query/ReseachQuery";
import { RootState } from "../../store";
import { notify } from "../../utils/toast";
import ModalViewListAwb from "../modal/ModalViewListAwb";
import DrawerCreatePurchaseRequest from "./DrawerCreatePurchaseRequest";
import DrawerEditPurchaseRequest from "./DrawerEditPurchaseRequest";

interface PurchaseRequestTabProps {
  alias: string;
}

const PurchaseRequestTab = ({ alias }: PurchaseRequestTabProps) => {
  const [materialId, setMaterialId] = useState<number>();
  const [searchPartNumber, setSearchPartNumber] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const { data: project } = useGetResearchProject(alias);
  const { data, isLoading } = useGetResearchProjectPurchaseRequest(
    project?.id || 0,
    debouncedSearch || undefined,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 500),
    [],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchPartNumber(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] =
    useState<IResearchPurchaseRequest | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const requestDirectorMutation = useRequestPurchaseRequestForDirector();
  const approvedByDirectorMutation = useApprovePurchaseRequestByDirector();
  const createPurchaseRequest = useCreatePurchaseRequest();
  const updatePurchaseRequest = useUpdateResearchProjectPurchaseRequest();

  const handleRequestDirector = async (requestId: number) => {
    try {
      await requestDirectorMutation.mutateAsync(requestId);
      notify.success("Đã gửi yêu cầu duyệt cho Giám đốc");
    } catch (error) {
      notify.error("Gửi yêu cầu thất bại");
    }
  };

  const handleApprove = async (requestId: number) => {
    try {
      await approvedByDirectorMutation.mutateAsync(requestId);
      notify.success("Duyệt thành công");
    } catch (error) {
      notify.error("Duyệt thất bại");
    }
  };

  const getPurchaseStatus = (status: ResearchPurchaseRequestStatus) => {
    switch (status) {
      case ResearchPurchaseRequestStatus.PENDING:
        return (
          <Tag color="default" icon={<ClockCircleOutlined />}>
            Chờ mua
          </Tag>
        );
      case ResearchPurchaseRequestStatus.PURCHASING:
        return (
          <Tag color="processing" icon={<ShoppingCartOutlined />}>
            Đang mua
          </Tag>
        );
      case ResearchPurchaseRequestStatus.PURCHASED:
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            Đã mua
          </Tag>
        );
      case ResearchPurchaseRequestStatus.REJECT:
        return (
          <Tag color="error" icon={<CloseCircleOutlined />}>
            Từ chối
          </Tag>
        );
      default:
        return null;
    }
  };
  const getApprovalStatus = (request: IResearchPurchaseRequest) => {
    if (request.isApprovedByDirector && request.isApprovedByPurchase) {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Đã duyệt
        </Tag>
      );
    }

    if (request.isRequestedDirector && !request.isApprovedByDirector) {
      return (
        <Tag color="warning" icon={<ClockCircleOutlined />}>
          Chờ BGĐ duyệt
        </Tag>
      );
    }
    if (request.isApprovedByDirector) {
      return (
        <Tag color="processing" icon={<CheckCircleOutlined />}>
          Giám đốc đã duyệt
        </Tag>
      );
    }
    return <Tag color="default">Nháp</Tag>;
  };
  const getPriorityRequest = (priority: PriorityMark) => {
    if (priority === PriorityMark.HIGH)
      return <Tag color="red">Mức độ: Rất gấp</Tag>;
    if (priority === PriorityMark.MEDIUM)
      return <Tag color="orange">Mức độ: Gấp</Tag>;
    return <Tag color="green">Mức độ: Thường</Tag>;
  };

  const getActionButtons = (request: IResearchPurchaseRequest) => {
    const buttons = [];
    if (
      !request.isRequestedDirector &&
      !(userInfo?.department?.departmentCode === DepartmentCode.CEO)
    ) {
      buttons.push(
        <Popconfirm
          key="request-director"
          title="Gửi yêu cầu duyệt cho Giám đốc"
          description="Bạn có chắc chắn muốn gửi yêu cầu này?"
          onConfirm={() => handleRequestDirector(request.id)}
          okText="Xác nhận"
          cancelText="Hủy"
        >
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            className="bg-orange-500"
            loading={requestDirectorMutation.isPending}
          >
            Gửi yêu cầu BGĐ
          </Button>
        </Popconfirm>,
      );
    }
    if (!request.isApprovedByPurchase) {
      buttons.push(
        <Button
          key="edit"
          type="primary"
          size="small"
          icon={<EditOutlined />}
          className="bg-yellow-500"
          onClick={() => {
            setSelectedRequest(request);
            setEditDrawerOpen(true);
          }}
        >
          Chỉnh sửa
        </Button>,
      );
    }

    return buttons;
  };

  const materialColumns: TableColumnType<MaterialItem>[] = [
    {
      title: "AWB",
      dataIndex: "awb",
      key: "awb",
      width: 120,
      render(_, record) {
        return (
          <Button
            type="link"
            onClick={() => {
              setMaterialId(record.id);
              setIsModalOpen(true);
            }}
            className="text-blue-600"
          >
            Xem
          </Button>
        );
      },
      align: "center",
    },
    {
      title: "Ngày PR",
      dataIndex: "datePr",
      key: "datePr",
      width: 110,
      render: (value: Date) => new Date(value).toLocaleDateString("vi-VN"),
      align: "center",
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 120,
      fixed: "left" as const,
      align: "center",
    },
    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
      width: 120,
      align: "center",
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      width: 120,
      align: "center",
    },
    {
      title: "Danh mục",
      dataIndex: "category",
      key: "category",
      width: 120,
      align: "center",
    },
    {
      title: "Nhà sản xuất",
      dataIndex: "manufacturer",
      key: "manufacturer",
      width: 150,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      align: "center",
    },
    {
      title: "Đơn vị",
      dataIndex: "unit",
      key: "unit",
      width: 60,
      align: "center" as const,
    },
    {
      title: "Nguồn",
      dataIndex: "source",
      key: "source",
      width: 120,
      ellipsis: true,
      align: "center",
    },
    {
      title: "CRD",
      dataIndex: "crd",
      key: "crd",
      width: 100,
      align: "center",
    },
    {
      title: "Số lượng",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "center",
    },
    {
      title: "Đơn giá EXW (USD)",
      dataIndex: "unitPriceExwUsd",
      key: "unitPriceExwUsd",
      width: 130,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("en-US"),
    },
    {
      title: "Tổng EXW (USD)",
      dataIndex: "totalPriceExwUsd",
      key: "totalPriceExwUsd",
      width: 130,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("en-US"),
    },
    {
      title: "Đơn giá (VND)",
      dataIndex: "unitPriceVnd",
      key: "unitPriceVnd",
      width: 140,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("vi-VN"),
    },
    {
      title: "Tổng (VND)",
      dataIndex: "totalPriceVnd",
      key: "totalPriceVnd",
      width: 140,
      align: "right",
      render: (value: number) => (
        <strong>{Number(value).toLocaleString("vi-VN")}</strong>
      ),
    },
    {
      title: "Phí ship (VND)",
      dataIndex: "shippingVnd",
      key: "shippingVnd",
      width: 140,
      align: "right",
      render: (value: number) => Number(value).toLocaleString("vi-VN"),
    },
    {
      title: "Thuế NK(%)",
      dataIndex: "importTax",
      key: "importTax",
      width: 140,
      align: "right",
      render: (value: number) => `${Number(value).toLocaleString("en-US")} %`,
    },
    {
      title: "Thuế NK (VNĐ)",
      dataIndex: "totalImportTaxVnd",
      key: "totalImportTaxVnd",
      width: 140,
      align: "right",
      render: (value: number) => `${Number(value).toLocaleString("vi-VN")}`,
    },
    {
      title: "Tổng cộng (VND)",
      dataIndex: "grandTotalVnd",
      key: "grandTotalVnd",
      width: 160,
      align: "right",
      render: (value: number) => (
        <strong className="text-blue-600">
          {Number(value).toLocaleString("vi-VN")}
        </strong>
      ),
    },
    {
      title: "Link",
      dataIndex: "link",
      key: "link",
      width: 80,
      render: (value: string) =>
        value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            <EyeOutlined />
          </a>
        ) : (
          "-"
        ),
      align: "center",
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Input
          placeholder="Tìm kiếm yêu cầu mua theo Part Number"
          value={searchPartNumber}
          onChange={handleSearchChange}
          allowClear
          style={{ width: "100%" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-green-600"
            onClick={() => setDrawerOpen(true)}
          >
            Tạo đề xuất mới
          </Button>
        </Space>
      </div>

      <Spin spinning={isLoading} className="flex items-center align-middle">
        {data?.projectBudgetCategories.map((category) => (
          <Card
            key={category.id}
            title={
              <div className="flex justify-between items-center">
                <span className="font-semibold">{category.categoryName}</span>
                <div className="text-sm font-normal text-gray-500">
                  Ngân sách:{" "}
                  {Number(category.budgetedAmount).toLocaleString("vi-VN")} VND
                  {" | "}
                  Đã chi: {Number(category.spentAmount).toLocaleString(
                    "vi-VN",
                  )}{" "}
                  VND
                </div>
              </div>
            }
            size="small"
          >
            {!category.researchPurchaseRequests ||
            category.researchPurchaseRequests.length === 0 ? (
              <Empty
                description="Chưa có đề xuất nào trong hạng mục này"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Collapse
                activeKey={expandedKeys}
                onChange={(keys) => setExpandedKeys(keys as string[])}
                items={category.researchPurchaseRequests.map((request) => ({
                  key: request.id.toString(),
                  label: (
                    <div className="flex justify-between items-center w-full">
                      <Space>
                        <span className="font-medium">
                          Đề xuất #{request.id}
                        </span>
                        {getApprovalStatus(request)}
                        {getPurchaseStatus(request.status)}
                        {getPriorityRequest(request.priority)}
                      </Space>
                      <Space className="text-sm text-gray-500 mr-2">
                        <span>Người tạo: {request.requestBy}</span>
                        <span>|</span>
                        <span>
                          Ngày tạo:{" "}
                          {new Date(request.requestDate).toLocaleDateString(
                            "vi-VN",
                          )}
                        </span>
                        <span>|</span>
                        <span>Tỉ giá: {request.exchangeRate}</span>
                        <span>|</span>
                        <span className="font-semibold text-blue-600">
                          {Number(request.proposalVnd).toLocaleString("vi-VN")}{" "}
                          VND
                        </span>
                      </Space>
                    </div>
                  ),
                  extra: (
                    <Space size="small" onClick={(e) => e.stopPropagation()}>
                      {getActionButtons(request)}
                      {userInfo?.department.departmentCode ===
                        DepartmentCode.CEO && (
                        <Popconfirm
                          title="Duyệt đề xuất"
                          description="Bạn có chắc chắn muốn duyệt đề xuất này?"
                          onConfirm={() => handleApprove(request.id)}
                          okText="Xác nhận"
                          cancelText="Hủy"
                        >
                          <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            loading={approvedByDirectorMutation.isPending}
                            disabled={
                              request.isApprovedByDirector ||
                              !request.isRequestedDirector
                            }
                          >
                            {request.isApprovedByDirector
                              ? "Đã duyệt"
                              : "Duyệt"}
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  ),
                  children: (
                    <div className="space-y-3">
                      {request.note && (
                        <div className="p-3 rounded">
                          <div className="text-xs text-gray-500 mb-1">
                            Ghi chú:
                          </div>
                          <div className="text-sm text-red-500">
                            {request.note}
                          </div>
                        </div>
                      )}
                      <Table
                        columns={materialColumns}
                        dataSource={request.listMaterials}
                        rowKey={(record) => record.id}
                        pagination={false}
                        scroll={{ x: 1500 }}
                        size="small"
                        summary={(pageData) => {
                          const totalVnd = pageData.reduce(
                            (sum, item) => sum + Number(item.grandTotalVnd),
                            0,
                          );
                          return (
                            <Table.Summary.Row className="font-semibold">
                              <Table.Summary.Cell
                                index={0}
                                colSpan={19}
                                align="right"
                              >
                                Tổng cộng:
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={1} align="right">
                                {totalVnd.toLocaleString("vi-VN")} VND
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={2} />
                            </Table.Summary.Row>
                          );
                        }}
                      />
                    </div>
                  ),
                }))}
              />
            )}
          </Card>
        ))}
      </Spin>

      {project && data && (
        <>
          <DrawerCreatePurchaseRequest
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            projectId={project.id}
            budgetCategories={data.projectBudgetCategories.map((cat) => ({
              id: cat.id,
              categoryName: cat.categoryName,
              budgetedAmount: cat.budgetedAmount,
              spentAmount: cat.spentAmount,
            }))}
            onSubmit={async (data) => {
              try {
                await createPurchaseRequest.mutateAsync(data);
                notify.success("Tạo đề xuất thành công");
                setDrawerOpen(false);
              } catch (error) {
                notify.error("Có lỗi xảy ra");
              }
            }}
          />
          <DrawerEditPurchaseRequest
            open={editDrawerOpen}
            onClose={() => {
              setEditDrawerOpen(false);
              setSelectedRequest(null);
            }}
            purchaseRequest={selectedRequest}
            onSubmit={async (id, data) => {
              try {
                await updatePurchaseRequest.mutateAsync({ id, data });
                notify.success("Cập nhật đề xuất thành công");
                setEditDrawerOpen(false);
                setSelectedRequest(null);
              } catch (error) {
                notify.error("Có lỗi xảy ra");
              }
            }}
            loading={updatePurchaseRequest.isPending}
          />
        </>
      )}
      {materialId !== undefined && (
        <ModalViewListAwb
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          materialId={materialId}
        />
      )}
    </div>
  );
};

export default PurchaseRequestTab;
